import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useStore } from '../store';
import type { AITrainerSession, Game, Goal, Profile } from '../types';
import { Brain, ArrowLeft, Target, Calendar, ExternalLink, Play, Dumbbell, Lightbulb, Heart } from 'lucide-react';
import GoalDiagram from '../components/GoalDiagram';
import GoalAnalytics from '../components/GoalAnalytics';
import ShotPositionDiagram from '../components/ShotPositionDiagram';
import ShotPositionAnalytics from '../components/ShotPositionAnalytics';
import { useTranslation } from '../i18n/useTranslation';

export default function AITrainerSession() {
  const { t } = useTranslation();
  const { sessionId } = useParams();
  const user = useStore((state) => state.user);
  const [session, setSession] = useState<AITrainerSession | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchSessionData = async () => {
      if (!sessionId || !user) return;

      try {
        // Fetch session
        const sessionDoc = await getDoc(doc(db, 'ai_trainer_sessions', sessionId));
        if (!sessionDoc.exists()) {
          setError(t('aiTrainerSession.sessionNotFound'));
          setLoading(false);
          return;
        }

        const sessionData = {
          id: sessionDoc.id,
          ...sessionDoc.data(),
          createdAt: sessionDoc.data().createdAt.toDate(),
          updatedAt: sessionDoc.data().updatedAt.toDate()
        } as AITrainerSession;

        // Verify user owns this session
        if (sessionData.userId !== user.uid) {
          setError(t('aiTrainerSession.permissionDenied'));
          setLoading(false);
          return;
        }

        setSession(sessionData);

        // Fetch games
        const gamesData: Game[] = [];
        for (const gameId of sessionData.gameIds) {
          const gameDoc = await getDoc(doc(db, 'games', gameId));
          if (gameDoc.exists()) {
            gamesData.push({
              id: gameDoc.id,
              ...gameDoc.data(),
              date: new Date(gameDoc.data().date),
              createdAt: new Date(gameDoc.data().createdAt)
            } as Game);
          }
        }
        setGames(gamesData);

        // Fetch goals for all games
        const allGoals: Goal[] = [];
        for (const game of gamesData) {
          const goalsQuery = query(
            collection(db, 'goals'),
            where('gameId', '==', game.id)
          );
          const goalsSnapshot = await getDocs(goalsQuery);
          const gameGoals = goalsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: new Date(doc.data().createdAt)
          })) as Goal[];
          allGoals.push(...gameGoals);
        }
        setGoals(allGoals);

        // Fetch user profile
        try {
          const profileDoc = await getDoc(doc(db, 'profiles', user.uid));
          if (profileDoc.exists()) {
            setProfile({
              id: profileDoc.id,
              ...profileDoc.data(),
              createdAt: profileDoc.data().createdAt.toDate()
            } as Profile);
          }
        } catch (err) {
          console.warn('Could not fetch profile:', err);
        }

      } catch (err) {
        console.error('Error fetching session data:', err);
        setError(t('aiTrainerSession.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    fetchSessionData();
  }, [sessionId, user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1b3968] mb-4"></div>
          <p className="text-gray-600">{t('aiTrainerSession.loadingSession')}</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error || t('aiTrainerSession.sessionNotFound')}
        </div>
      </div>
    );
  }

  return (
    <div className='max-w-6xl mx-auto px-4 py-8'>
      <div className='flex items-center gap-4 mb-8'>
        <Link
          to='/ai-trainer'
          className='inline-flex items-center text-[#1b3968] hover:text-[#1b3968]/80'
        >
          <ArrowLeft className='h-4 w-4 mr-1' />
          {t('aiTrainerSession.backToAITrainer')}
        </Link>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
        {/* Main Content */}
        <div className='lg:col-span-2 space-y-8'>
          {/* Session Header */}
          <div className='bg-white shadow rounded-lg p-6'>
            <div className='flex items-start gap-4'>
              <div className='w-12 h-12 bg-[#1b3968]/10 rounded-full flex items-center justify-center flex-shrink-0'>
                <Brain className='h-6 w-6 text-[#1b3968]' />
              </div>
              <div className='flex-1'>
                <h1 className='text-2xl font-bold text-primary-600 mb-2 italic'>
                  {session.title}
                </h1>
                <div className='flex items-center gap-4 text-sm text-gray-600 mb-4'>
                  <div className='flex items-center gap-1'>
                    <Calendar className='h-4 w-4' />
                    <span>{session.createdAt.toLocaleDateString()}</span>
                  </div>
                  <div className='flex items-center gap-1'>
                    <Target className='h-4 w-4' />
                    <span>
                      {games.length} {games.length !== 1 ? t('nav.games').toLowerCase() : t('nav.games').toLowerCase().slice(0, -1)}{' '}
                      {t('aiTrainerSession.analyzed')}
                    </span>
                  </div>
                </div>

                {games.length > 0 && (
                  <div className='mb-4'>
                    <p className='text-sm font-medium text-gray-700 mb-2'>
                      {t('aiTrainerSession.gamesAnalyzed')}:
                    </p>
                    <div className='flex flex-wrap gap-2'>
                      {games.map((game) => (
                        <Link
                          key={game.id}
                          to={`/game/${game.id}`}
                          className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800 hover:bg-primary-200 transition-colors'
                        >
                          {t('aiTrainerSession.vs')} {game.opponent} ({game.date.toLocaleDateString()})
                          <ExternalLink className='h-3 w-3 ml-1' />
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* AI Analysis */}
          <div className='bg-white shadow rounded-lg p-6'>
            <h2 className='text-xl font-semibold mb-4 flex items-center gap-2'>
              <Brain className='h-5 w-5 text-[#1b3968]' />
              {t('aiTrainerSession.aiCoachAnalysis')}
            </h2>
            <div className='prose prose-gray max-w-none'>
              <p className='text-gray-700 whitespace-pre-wrap leading-relaxed'>
                {session.analysis}
              </p>
            </div>
          </div>

          {/* Recommended Drills */}
          {session.drills.length > 0 && (
            <div className='bg-white shadow rounded-lg p-6'>
              <h2 className='text-xl font-semibold mb-4 flex items-center gap-2'>
                <Dumbbell className='h-5 w-5 text-[#1b3968]' />
                {t('aiTrainerSession.recommendedDrills')}
              </h2>
              <div className='space-y-4'>
                {session.drills.map((drill, index) => (
                  <div
                    key={index}
                    className='border border-gray-200 rounded-lg p-4'
                  >
                    <div className='flex items-start justify-between'>
                      <div className='flex-1'>
                        <h3 className='font-semibold text-gray-900 mb-2'>
                          {drill.title}
                        </h3>
                        <p className='text-gray-600 mb-3'>
                          {drill.description}
                        </p>
                        <div className='flex flex-wrap gap-2 mb-3'>
                          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800'>
                            {drill.zone}
                          </span>
                          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800'>
                            {drill.goalType}
                          </span>
                        </div>
                      </div>
                    </div>
                    {drill.videoUrl && (
                      <a
                        href={drill.videoUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='inline-flex items-center text-[#1b3968] hover:text-[#1b3968]/80 font-medium'
                      >
                        <Play className='h-4 w-4 mr-1' />
                        {t('aiTrainerSession.watchVideo')}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Performance Tips */}
          {session.tips.length > 0 && (
            <div className='bg-white shadow rounded-lg p-6'>
              <h2 className='text-xl font-semibold mb-4 flex items-center gap-2'>
                <Lightbulb className='h-5 w-5 text-[#1b3968]' />
                {t('aiTrainerSession.performanceTips')}
              </h2>
              <div className='space-y-3'>
                {session.tips.map((tip, index) => (
                  <div key={index} className='flex items-start gap-3'>
                    <div className='w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5'>
                      <span className='text-yellow-600 text-sm font-semibold'>
                        {index + 1}
                      </span>
                    </div>
                    <p className='text-gray-700'>{tip}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Motivational Note */}
          {session.motivationalNote && (
            <div className='bg-gradient-to-r from-green-50 to-primary-50 border border-green-200 rounded-lg p-6'>
              <h2 className='text-xl font-semibold mb-4 flex items-center gap-2 text-green-800'>
                <Heart className='h-5 w-5' />
                {t('aiTrainerSession.motivationalNote')}
              </h2>
              <p className='text-green-700 italic leading-relaxed'>
                "{session.motivationalNote}"
              </p>
            </div>
          )}
        </div>

        {/* Sidebar - Game Visualizations */}
        <div className='space-y-6'>
          <div className='bg-white shadow rounded-lg p-6'>
            <h3 className='text-lg font-semibold mb-4'>{t('aiTrainerSession.goalDistribution')}</h3>
            <GoalAnalytics goals={goals} width={280} height={210} />
          </div>

          <div className='bg-white shadow rounded-lg p-6'>
            <h3 className='text-lg font-semibold mb-4'>{t('aiTrainerSession.goalLocations')}</h3>
            <GoalDiagram
              onPositionSelect={() => {}}
              goals={goals}
              width={280}
              height={210}
              readOnly={true}
              hideNumbers={true}
            />
          </div>

          <div className='bg-white shadow rounded-lg p-6'>
            <h3 className='text-lg font-semibold mb-4'>{t('aiTrainerSession.shotDistribution')}</h3>
            <ShotPositionAnalytics goals={goals} width={280} height={210} />
          </div>

          <div className='bg-white shadow rounded-lg p-6'>
            <h3 className='text-lg font-semibold mb-4'>{t('aiTrainerSession.shotLocations')}</h3>
            <ShotPositionDiagram
              onPositionSelect={() => {}}
              goals={goals}
              width={280}
              height={210}
              readOnly={true}
              hideNumbers={true}
            />
          </div>

          {/* Game Stats Summary */}
          <div className='bg-white shadow rounded-lg p-6'>
            <h3 className='text-lg font-semibold mb-4'>{t('aiTrainerSession.sessionStats')}</h3>
            <div className='space-y-3'>
              <div className='flex justify-between'>
                <span className='text-gray-600'>{t('aiTrainerSession.totalGoals')}:</span>
                <span className='font-semibold'>{goals.length}</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-gray-600'>{t('aiTrainerSession.gamesAnalyzed')}:</span>
                <span className='font-semibold'>{games.length}</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-gray-600'>{t('aiTrainerSession.drillsRecommended')}:</span>
                <span className='font-semibold'>{session.drills.length}</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-gray-600'>{t('aiTrainerSession.tipsProvided')}:</span>
                <span className='font-semibold'>{session.tips.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}